import type { LocaleCode, LocalizedString } from "@/types/sections";

export type LegalSlug = "cgu" | "privacy" | "cookies" | "data-policy";

type LegalSection = {
  title: LocalizedString;
  content: LocalizedString;
};

export type LegalDocument = {
  slug: LegalSlug;
  navLabel: LocalizedString;
  title: LocalizedString;
  summary: LocalizedString;
  lastUpdated: LocalizedString;
  sections: LegalSection[];
};

export const LOCALIZED_LEGAL_SLUGS: Record<LegalSlug, { en: string; fr: string }> = {
  cgu: {
    en: "terms-of-service",
    fr: "conditions-generales",
  },
  privacy: {
    en: "privacy-policy",
    fr: "politique-confidentialite",
  },
  cookies: {
    en: "cookies-policy",
    fr: "politique-cookies",
  },
  "data-policy": {
    en: "data-collection-and-processing-policy",
    fr: "politique-collecte-et-traitement-des-donnees",
  },
};

const LEGACY_LEGAL_ALIASES: Record<string, LegalSlug> = {
  // Old indexed paths with underscores
  politique_de_collecte_et_de_traitement_des_donnees_personnelles: "data-policy",
  politique_de_confidentialite: "privacy",
  conditions_generales: "cgu",
  // Old hyphenated variants
  "politique-de-collecte-et-de-traitement-des-donnees-personnelles": "data-policy",
  "politique-de-confidentialite": "privacy",
};

export const LEGAL_DOCUMENTS: Record<LegalSlug, LegalDocument> = {
  cgu: {
    slug: "cgu",
    navLabel: {
      en: "Terms of Service",
      fr: "Conditions g\u00e9n\u00e9rales",
    },
    title: {
      en: "Uncommon 12 General Conditions of Use",
      fr: "Conditions g\u00e9n\u00e9rales d'utilisation de Uncommon 12",
    },
    summary: {
      en: "Uncommon 12 offers members curated services and privileges in Geneva. Using the app implies acceptance of these terms.",
      fr: "Uncommon 12 propose \u00e0 ses membres des services et privil\u00e8ges \u00e0 Gen\u00e8ve. L'utilisation de l'application implique l'acceptation des pr\u00e9sentes conditions.",
    },
    lastUpdated: {
      en: "Done in Geneva, October 14, 2024",
      fr: "Fait \u00e0 Gen\u00e8ve, le 14 octobre 2024",
    },
    sections: [
      {
        title: {
          en: "1. App overview",
          fr: "1. Pr\u00e9sentation de l'application",
        },
        content: {
          en: "Uncommon 12 is an application offering its members a set of services and products, including privileges that can be obtained from partners in Geneva, Switzerland. By using this application, you accept these General Conditions of Use (CGU).",
          fr: "Uncommon 12 est une application proposant \u00e0 ses membres un ensemble de services et produits, notamment des privil\u00e8ges qui peuvent \u00eatre obtenus aupr\u00e8s de partenaires \u00e0 Gen\u00e8ve, en Suisse. En utilisant cette application, vous acceptez les pr\u00e9sentes Conditions G\u00e9n\u00e9rales d'Utilisation (CGU).",
        },
      },
      {
        title: {
          en: "2. Data collection and use",
          fr: "2. Collecte et utilisation des donn\u00e9es",
        },
        content: {
          en: "We collect personal information such as your first name, last name, email address, postal address, etc., in order to register you as a member of the Club and to communicate with you when necessary. This data is stored securely and will only be shared with third parties for its intended purposes, such as organizing events or sending products to your home.",
          fr: "Nous collectons des informations personnelles telles que votre pr\u00e9nom, nom, adresse e-mail, adresse postale, etc., afin de vous inscrire en tant que membre du Club et de communiquer avec vous lorsque cela est n\u00e9cessaire. Ces donn\u00e9es sont stock\u00e9es de mani\u00e8re s\u00e9curis\u00e9e et ne seront partag\u00e9es avec des tiers uniquement dans le cadre des finalit\u00e9s pr\u00e9vues, telles que l'organisation d'\u00e9v\u00e9nements ou l'envoi de produits \u00e0 votre domicile.",
        },
      },
      {
        title: {
          en: "3. User consent",
          fr: "3. Consentement de l'utilisateur",
        },
        content: {
          en: "By using our application, you consent to the collection and use of your personal data in accordance with our privacy policy.",
          fr: "En utilisant notre application, vous consentez \u00e0 la collecte et \u00e0 l'utilisation de vos donn\u00e9es personnelles conform\u00e9ment \u00e0 notre politique de confidentialit\u00e9.",
        },
      },
      {
        title: {
          en: "4. User rights and obligations",
          fr: "4. Droits et obligations de l'utilisateur",
        },
        content: {
          en: "The user undertakes to use the application in compliance with the laws in force and these terms. Any abusive, fraudulent, or illegal use may result in suspension of access to the application.",
          fr: "L'utilisateur s'engage \u00e0 utiliser l'application dans le respect des lois en vigueur et des pr\u00e9sentes CGU. Toute utilisation abusive, frauduleuse ou ill\u00e9gale peut entra\u00eener la suspension de l'acc\u00e8s \u00e0 l'application.",
        },
      },
      {
        title: {
          en: "5. Advertising and in-app purchases",
          fr: "5. Publicit\u00e9 et achats int\u00e9gr\u00e9s",
        },
        content: {
          en: "The application may display advertisements. You can also make in-app purchases. Please read descriptions carefully before making a purchase.",
          fr: "L'application peut afficher des publicit\u00e9s. Vous pouvez \u00e9galement effectuer des achats int\u00e9gr\u00e9s dans l'application. Veuillez lire attentivement les descriptions avant d'effectuer un achat.",
        },
      },
      {
        title: {
          en: "6. Intellectual property",
          fr: "6. Propri\u00e9t\u00e9 intellectuelle",
        },
        content: {
          en: "All content of the application (texts, images, logos, etc.) are protected by copyright. Any reproduction or distribution without prior authorization is prohibited.",
          fr: "Tous les contenus de l'application (textes, images, logos, etc.) sont prot\u00e9g\u00e9s par le droit d'auteur. Toute reproduction ou diffusion sans autorisation pr\u00e9alable est interdite.",
        },
      },
      {
        title: {
          en: "7. Updates and changes to the terms",
          fr: "7. Mises \u00e0 jour et modifications des CGU",
        },
        content: {
          en: "We reserve the right to modify these terms at any time. Users will be notified of changes via an in-app notification.",
          fr: "Nous nous r\u00e9servons le droit de modifier les pr\u00e9sentes CGU \u00e0 tout moment. Les utilisateurs seront inform\u00e9s des modifications via une notification dans l'application.",
        },
      },
      {
        title: {
          en: "8. Limitation of liability",
          fr: "8. Limitation de responsabilit\u00e9",
        },
        content: {
          en: "Uncommon 12 is provided \"as is\" without any performance guarantee. We are not responsible for any loss or damage that may arise from the use of the application.",
          fr: "Uncommon 12 est fourni \"en l'\u00e9tat\" sans aucune garantie de performance. Nous ne sommes pas responsables des pertes ou dommages pouvant r\u00e9sulter de l'utilisation de l'application.",
        },
      },
      {
        title: {
          en: "9. Contact",
          fr: "9. Contact",
        },
        content: {
          en: "For any questions or requests regarding the application, you can contact us at the following address: <a href='mailto:contact@uncommon12.ch'>contact@uncommon12.ch</a>.",
          fr: "Pour toute question ou demande concernant l'application, vous pouvez nous contacter \u00e0 l'adresse suivante : <a href='mailto:contact@uncommon12.ch'>contact@uncommon12.ch</a>.",
        },
      },
    ],
  },
  privacy: {
    slug: "privacy",
    navLabel: {
      en: "Privacy policy",
      fr: "Politique de confidentialit\u00e9",
    },
    title: {
      en: "Privacy Policy",
      fr: "Politique de confidentialit\u00e9",
    },
    summary: {
      en: "How Uncommon12 collects, uses, and protects personal information for its application, services, and communications.",
      fr: "Comment Uncommon12 collecte, utilise et prot\u00e8ge les informations personnelles pour son application, ses services et ses communications.",
    },
    lastUpdated: {
      en: "This privacy policy was updated on May 1, 2024",
      fr: "Cette politique de confidentialit\u00e9 a \u00e9t\u00e9 mise \u00e0 jour le 1er mai 2024",
    },
    sections: [
      {
        title: {
          en: "Introduction",
          fr: "Introduction",
        },
        content: {
          en: "This Privacy Policy governs the manner in which Uncommon12 (\"the Application\") collects, uses, maintains, and discloses information from users (\"Users\") of the Application. This privacy policy applies to the Application and all products and services offered by the Application.",
          fr: "Cette Politique de Confidentialit\u00e9 r\u00e9git la mani\u00e8re dont Uncommon12 (ci-apr\u00e8s \"l'Application\") collecte, utilise, maintient et divulgue les informations des utilisateurs (\"Utilisateurs\") de l'Application. Cette politique de confidentialit\u00e9 s'applique \u00e0 l'Application et \u00e0 tous les produits et services propos\u00e9s par l'Application.",
        },
      },
      {
        title: {
          en: "Personal information",
          fr: "Informations personnelles",
        },
        content: {
          en: "Personal data includes first names, last names, email addresses, postal addresses, and telephone numbers of Users. We may collect personal information from Users in different ways, including when they visit our Application, register on the Application, place an order, subscribe to the newsletter, respond to a survey, fill out a form, and in relation to other activities, services, features, or resources that we make available on our Application.",
          fr: "Par donn\u00e9es personnelles, l'on entend notamment les pr\u00e9noms, noms, adresses mail, adresse postale, num\u00e9ro de t\u00e9l\u00e9phone des Utilisateurs. Nous pouvons collecter des informations personnelles aupr\u00e8s des Utilisateurs de diff\u00e9rentes mani\u00e8res, notamment lorsqu'ils visitent notre Application, s'inscrivent sur l'Application, passent une commande, s'abonnent \u00e0 la newsletter, r\u00e9pondent \u00e0 un sondage, remplissent un formulaire et en relation avec d'autres activit\u00e9s, services, fonctionnalit\u00e9s ou ressources que nous mettons \u00e0 disposition sur notre Application.",
        },
      },
      {
        title: {
          en: "Use of collected information",
          fr: "Utilisation des informations collect\u00e9es",
        },
        content: {
          en: "We may collect and use Users' personal information for the following reasons: to personalize the user experience, to improve our Application, to process transactions, to send periodic emails, to respond to questions, concerns, or other requests from Users, and to manage content, promotions, surveys, or other functionality of the Application.",
          fr: "Nous pouvons collecter et utiliser les informations personnelles des Utilisateurs pour les raisons suivantes : pour personnaliser l'exp\u00e9rience utilisateur, pour am\u00e9liorer notre Application, pour traiter les transactions, pour envoyer des e-mails p\u00e9riodiques, pour r\u00e9pondre aux questions, pr\u00e9occupations ou autres demandes des Utilisateurs, pour g\u00e9rer un contenu, une promotion, une enqu\u00eate ou une autre fonctionnalit\u00e9 de l'Application.",
        },
      },
      {
        title: {
          en: "Protection of information",
          fr: "Protection des informations",
        },
        content: {
          en: "We adopt appropriate and proportionate personal data collection, storage, and processing practices, and security measures to protect against unauthorized access, alteration, disclosure, or destruction of personal information.",
          fr: "Nous adoptons des pratiques appropri\u00e9es et proportionn\u00e9es de collecte, de stockage et de traitement des donn\u00e9es personnelles, ainsi que les mesures de s\u00e9curit\u00e9 adapt\u00e9es pour les prot\u00e9ger contre l'acc\u00e8s non autoris\u00e9, l'alt\u00e9ration, la divulgation ou la destruction de vos informations personnelles.",
        },
      },
      {
        title: {
          en: "Sharing of personal information",
          fr: "Partage de vos informations personnelles",
        },
        content: {
          en: "We do not sell, trade, or rent Users' personal information to third parties. We may share generic aggregated demographic information not linked to personally identifiable information about visitors and Users with our business partners, trusted affiliates, and advertisers for the purposes described above.",
          fr: "Nous ne vendons, n'\u00e9changeons pas ni ne louons les informations personnelles des Utilisateurs \u00e0 des tiers. Nous pouvons partager des informations d\u00e9mographiques agr\u00e9g\u00e9es g\u00e9n\u00e9riques non li\u00e9es \u00e0 des informations personnelles identifiables concernant les visiteurs et les Utilisateurs avec nos partenaires commerciaux, affili\u00e9s de confiance et annonceurs \u00e0 des fins d\u00e9crites ci-dessus.",
        },
      },
      {
        title: {
          en: "Compliance with current legislation",
          fr: "Conformit\u00e9 \u00e0 la l\u00e9gislation en vigueur",
        },
        content: {
          en: "We undertake to comply with the provisions of Swiss legislation regarding the protection of personal data, in particular the Federal Data Protection Act (LPD).",
          fr: "Nous nous engageons \u00e0 respecter les dispositions de la l\u00e9gislation suisse en mati\u00e8re de protection des donn\u00e9es personnelles, notamment la Loi f\u00e9d\u00e9rale sur la protection des donn\u00e9es (LPD).",
        },
      },
      {
        title: {
          en: "Changes to this privacy policy",
          fr: "Modification de cette politique de confidentialit\u00e9",
        },
        content: {
          en: "We reserve the right to update this privacy policy at any time. We encourage Users to frequently check this page for any changes to stay informed about how we protect the personal information we collect. You acknowledge and agree that it is your responsibility to review this privacy policy periodically and stay informed of changes.",
          fr: "Nous nous r\u00e9servons le droit de mettre \u00e0 jour cette politique de confidentialit\u00e9 \u00e0 tout moment. Nous encourageons les Utilisateurs \u00e0 consulter fr\u00e9quemment cette page pour toute modification afin de rester inform\u00e9s sur la mani\u00e8re dont nous prot\u00e9geons les informations personnelles que nous collectons. Vous reconnaissez et acceptez qu'il est de votre responsabilit\u00e9 de consulter p\u00e9riodiquement cette politique de confidentialit\u00e9 et de vous informer des modifications.",
        },
      },
      {
        title: {
          en: "Your acceptance of these terms",
          fr: "Votre acceptation de ces termes",
        },
        content: {
          en: "By using this Application, you signify your acceptance of this policy. If you do not agree to this policy, please do not use our Application. Your use of the Application after publication of changes to our privacy policy will be considered as acceptance of those changes.",
          fr: "En utilisant cette Application, vous signifiez votre acceptation de cette politique. Si vous n'acceptez pas cette politique, veuillez ne pas utiliser notre Application. Votre utilisation de l'Application apr\u00e8s publication de modifications apport\u00e9es \u00e0 notre politique de confidentialit\u00e9 sera consid\u00e9r\u00e9e comme votre acceptation de ces modifications.",
        },
      },
      {
        title: {
          en: "Contact us",
          fr: "Contactez-nous",
        },
        content: {
          en: "If you have any questions regarding our privacy policy, the practices of this Application, or your dealings with this Application, please contact us at the following address: <a href=\"mailto:contact@uncommon12.ch\">contact@uncommon12.ch</a> or by telephone at <a href=\"tel:+41799181878\">+41 79 918 18 78</a>. Uncommon12 S\u00e0RL, Chemin des Cr\u00eats de Pregny 13, CH-1218 Grand-Saconnex.",
          fr: "Si vous avez des questions concernant notre politique de confidentialit\u00e9, les pratiques de cette Application ou vos transactions avec cette Application, veuillez nous contacter \u00e0 l'adresse suivante : <a href=\"mailto:contact@uncommon12.ch\">contact@uncommon12.ch</a> ou par t\u00e9l\u00e9phone au <a href=\"tel:+41799181878\">+41 79 918 18 78</a>. Uncommon12 S\u00e0RL, Chemin des Cr\u00eats de Pregny 13, CH-1218 Grand-Saconnex.",
        },
      },
    ],
  },
  cookies: {
    slug: "cookies",
    navLabel: {
      en: "Cookies policy",
      fr: "Politique des cookies",
    },
    title: {
      en: "Cookies Policy",
      fr: "Politique des cookies",
    },
    summary: {
      en: "How and why Uncommon12 uses cookies and similar technologies across its platform and applications.",
      fr: "Comment et pourquoi Uncommon12 utilise des cookies et technologies similaires sur sa plateforme et ses applications.",
    },
    lastUpdated: {
      en: "This cookies policy was updated on May 1, 2024",
      fr: "Cette politique de cookies a \u00e9t\u00e9 mise \u00e0 jour le 1er mai 2024",
    },
    sections: [
      {
        title: {
          en: "Introduction",
          fr: "Introduction",
        },
        content: {
          en: "This Cookies Policy explains how Uncommon12 uses cookies and similar technologies on its website <a href=\"https://www.uncommon12.ch\">www.uncommon12.ch</a> and its Uncommon12 application (collectively \"the Platform\"). By using the Platform, you consent to the use of cookies in accordance with this Cookies Policy.",
          fr: "Cette Politique de Cookies explique comment Uncommon12 utilise des cookies et des technologies similaires sur son site web <a href=\"https://www.uncommon12.ch\">www.uncommon12.ch</a> et son application Uncommon12 (ci-apr\u00e8s collectivement d\u00e9nomm\u00e9s \"la Plateforme\"). En utilisant la Plateforme, vous consentez \u00e0 l'utilisation de cookies conform\u00e9ment \u00e0 cette Politique de Cookies.",
        },
      },
      {
        title: {
          en: "What is a cookie?",
          fr: "Qu'est-ce qu'un cookie ?",
        },
        content: {
          en: "A cookie is a small text file placed on your device when you visit a website or use an application. Cookies can be temporary (session cookies) or permanent (persistent cookies). They may be used by the website or application to store information about your preferences, past interactions, and other technical details.",
          fr: "Un cookie est un petit fichier texte plac\u00e9 sur votre appareil lorsque vous visitez un site web ou utilisez une application. Les cookies peuvent \u00eatre temporaires (cookies de session) ou permanents (cookies persistants). Ils peuvent \u00eatre utilis\u00e9s par le site web ou l'application pour stocker des informations sur vos pr\u00e9f\u00e9rences, vos interactions pass\u00e9es et d'autres d\u00e9tails techniques.",
        },
      },
      {
        title: {
          en: "Why do we use cookies?",
          fr: "Pourquoi utilisons-nous des cookies ?",
        },
        content: {
          en: "We use cookies for various reasons, including to improve your user experience on the Platform, analyze use of the Platform and collect anonymous statistics, personalize the content and ads presented to you, and provide social media features or integrate third-party services.",
          fr: "Nous utilisons des cookies pour diverses raisons, notamment pour am\u00e9liorer votre exp\u00e9rience utilisateur sur la Plateforme, analyser l'utilisation de la Plateforme et recueillir des statistiques anonymes, personnaliser le contenu et les annonces qui vous sont pr\u00e9sent\u00e9s, fournir des fonctionnalit\u00e9s de m\u00e9dias sociaux et int\u00e9grer des services tiers.",
        },
      },
      {
        title: {
          en: "Types of cookies used",
          fr: "Types de cookies utilis\u00e9s",
        },
        content: {
          en: "We use the following types of cookies on our Platform: essential cookies (necessary for the Platform to function properly), performance cookies (help understand how you interact with the Platform), functionality cookies (allow the Platform to remember your choices and preferences), and advertising cookies (used to deliver relevant ads based on your interests and browsing behavior).",
          fr: "Nous utilisons les types de cookies suivants sur notre Plateforme : cookies essentiels (n\u00e9cessaires au bon fonctionnement de la Plateforme), cookies de performance (aident \u00e0 comprendre comment vous interagissez avec la Plateforme), cookies de fonctionnalit\u00e9 (permettent \u00e0 la Plateforme de se souvenir de vos choix et pr\u00e9f\u00e9rences), cookies de publicit\u00e9 (utilis\u00e9s pour diffuser des annonces pertinentes bas\u00e9es sur vos int\u00e9r\u00eats et comportement de navigation).",
        },
      },
      {
        title: {
          en: "Managing cookies",
          fr: "Gestion des cookies",
        },
        content: {
          en: "You can configure your browser to refuse all cookies or to alert you when cookies are sent. However, some parts of the Platform may not function properly if you disable cookies.",
          fr: "Vous pouvez configurer votre navigateur pour refuser tous les cookies ou pour vous alerter lorsque des cookies sont envoy\u00e9s. Toutefois, certaines parties de la Plateforme peuvent ne pas fonctionner correctement si vous d\u00e9sactivez les cookies.",
        },
      },
      {
        title: {
          en: "Consent",
          fr: "Consentement",
        },
        content: {
          en: "By using the Platform, you consent to the use of cookies in accordance with this Cookies Policy. If you do not consent, please do not use the Platform or configure your browser to block cookies.",
          fr: "En utilisant la Plateforme, vous consentez \u00e0 l'utilisation de cookies conform\u00e9ment \u00e0 cette Politique de Cookies. Si vous ne consentez pas \u00e0 l'utilisation de cookies, veuillez ne pas utiliser la Plateforme ou configurez votre navigateur pour bloquer les cookies.",
        },
      },
      {
        title: {
          en: "Contact",
          fr: "Contact",
        },
        content: {
          en: "If you have any questions regarding our use of cookies or if you wish to exercise your data protection rights, please contact us at the following address: <a href=\"mailto:contact@uncommon12.ch\">contact@uncommon12.ch</a> or by telephone at <a href=\"tel:+41799181878\">+41 79 918 18 78</a>. Uncommon12, Chemin des Cr\u00eats de Pregny 13, CH-1218 Grand-Saconnex.",
          fr: "Si vous avez des questions concernant notre utilisation des cookies ou si vous souhaitez exercer vos droits en mati\u00e8re de protection des donn\u00e9es, veuillez nous contacter \u00e0 l'adresse suivante : <a href=\"mailto:contact@uncommon12.ch\">contact@uncommon12.ch</a> ou par t\u00e9l\u00e9phone au <a href=\"tel:+41799181878\">+41 79 918 18 78</a>. Uncommon12, Chemin des Cr\u00eats de Pregny 13, CH-1218 Grand-Saconnex.",
        },
      },
    ],
  },
  "data-policy": {
    slug: "data-policy",
    navLabel: {
      en: "Data collection policy",
      fr: "Politique de collecte des donn\u00e9es",
    },
    title: {
      en: "Data Collection and Processing Policy",
      fr: "Politique de collecte et de traitement des donn\u00e9es",
    },
    summary: {
      en: "How Uncommon12 collects, shares, secures, and deletes membership data, along with member rights under Swiss law.",
      fr: "Comment Uncommon12 collecte, partage, s\u00e9curise et supprime les donn\u00e9es des membres, ainsi que les droits des membres selon la loi suisse.",
    },
    lastUpdated: {
      en: "This data collection and processing policy was updated on August 1, 2024",
      fr: "Cette politique de collecte et traitement des donn\u00e9es personnelles a \u00e9t\u00e9 mise \u00e0 jour le 1er ao\u00fbt 2024",
    },
    sections: [
      {
        title: {
          en: "Introduction",
          fr: "Introduction",
        },
        content: {
          en: "This personal data collection and processing policy describes how Uncommon12 collects, uses, and protects the data of members of the Uncommon 12 application.",
          fr: "Cette politique de collecte et de traitement des donn\u00e9es personnelles d\u00e9crit comment Uncommon12 collecte, utilise et prot\u00e8ge les donn\u00e9es des membres de l'application Uncommon 12.",
        },
      },
      {
        title: {
          en: "Purposes of data processing",
          fr: "Finalit\u00e9s du traitement des donn\u00e9es",
        },
        content: {
          en: "The personal data collected is used exclusively for the following purposes: membership management (creation and management of the member account, identity verification, and provision of associated services), communications (sending information on membership, newsletters, special offers, and important updates), improvement of the services (data analysis to improve the user experience and services offered to members), and compliance with legal obligations.",
          fr: "Les donn\u00e9es personnelles collect\u00e9es sont utilis\u00e9es exclusivement pour les finalit\u00e9s suivantes : gestion du membership (cr\u00e9ation et gestion du compte de membre, v\u00e9rification de l'identit\u00e9, et fourniture des services associ\u00e9s), communications (envoi d'informations sur le membership, des newsletters, des offres sp\u00e9ciales, et des mises \u00e0 jour importantes concernant les services), am\u00e9lioration des services (analyse des donn\u00e9es pour am\u00e9liorer l'exp\u00e9rience utilisateur et les services offerts aux membres), respect des obligations l\u00e9gales (conformit\u00e9 aux exigences l\u00e9gales et r\u00e9glementaires).",
        },
      },
      {
        title: {
          en: "Data sharing and transfer",
          fr: "Partage et transfert des donn\u00e9es",
        },
        content: {
          en: "Members' personal data will not be shared with third parties without the member's explicit consent, except in the following cases: performance of the contract (for the provision of contractual services related to membership) and legal obligations (when required by law, a court order, or a formal request from a competent authority).",
          fr: "Les donn\u00e9es personnelles des membres ne seront pas partag\u00e9es avec des tiers sans le consentement explicite du membre, sauf dans les cas suivants : ex\u00e9cution du contrat (pour la fourniture des services contractuels li\u00e9s au membership) et obligations l\u00e9gales (lorsque cela est requis par la loi, une ordonnance du tribunal, ou une demande officielle d'une autorit\u00e9 comp\u00e9tente).",
        },
      },
      {
        title: {
          en: "Data retention period",
          fr: "Dur\u00e9e de conservation des donn\u00e9es",
        },
        content: {
          en: "Personal data is retained for the duration of the membership and will be deleted or anonymized within 90 days following the termination or deletion of the membership, unless prolonged retention is necessary to comply with legal obligations or for the protection of our legitimate interests.",
          fr: "Les donn\u00e9es personnelles sont conserv\u00e9es pendant toute la dur\u00e9e du membership et seront supprim\u00e9es ou anonymis\u00e9es dans un d\u00e9lai de 90 jours suivant la r\u00e9siliation ou la suppression du membership, sauf si une conservation prolong\u00e9e est n\u00e9cessaire pour se conformer aux obligations l\u00e9gales ou pour la protection de nos int\u00e9r\u00eats l\u00e9gitimes.",
        },
      },
      {
        title: {
          en: "Rights of members",
          fr: "Droits des membres",
        },
        content: {
          en: "In accordance with the LPD, members have the following rights regarding their personal data: right of access (obtain information on personal data held by Uncommon 12 S\u00e0RL), right of rectification (request the correction of inaccurate or incomplete personal data), right of deletion (request the deletion of personal data, subject to legal obligations), right of restriction of processing (request the limitation of the processing of data in certain circumstances), and right of objection (object to the processing of data for legitimate reasons). Members can contact Uncommon 12 S\u00e0RL by email at <a href=\"mailto:contact@uncommon12.ch\">contact@uncommon12.ch</a> or by post at the following address: Uncommon 12 S\u00e0rl, Chemin des Cr\u00eats de Pregny 13, 1218 Grand-Saconnex.",
          fr: "Conform\u00e9ment \u00e0 la LPD, les membres disposent des droits suivants concernant leurs donn\u00e9es personnelles : droit d'acc\u00e8s (obtenir des informations sur les donn\u00e9es personnelles d\u00e9tenues par Uncommon 12 S\u00e0RL), droit de rectification (demander la correction de donn\u00e9es personnelles inexactes ou incompl\u00e8tes), droit de suppression (demander la suppression de ses donn\u00e9es personnelles, sous r\u00e9serve des obligations l\u00e9gales), droit de limitation du traitement (demander la limitation du traitement des donn\u00e9es dans certaines circonstances), droit d'opposition (s'opposer au traitement des donn\u00e9es pour des motifs l\u00e9gitimes). Les membres peuvent contacter Uncommon 12 S\u00e0RL par courriel \u00e0 <a href=\"mailto:contact@uncommon12.ch\">contact@uncommon12.ch</a> ou par courrier \u00e0 l'adresse suivante : Uncommon 12 S\u00e0rl, Chemin des Cr\u00eats de Pregny 13, 1218 Grand-Saconnex.",
        },
      },
      {
        title: {
          en: "Data security",
          fr: "S\u00e9curit\u00e9 des donn\u00e9es",
        },
        content: {
          en: "Uncommon12 S\u00e0RL implements appropriate technical and organizational measures to protect members' personal data against unauthorized access, loss, alteration, or destruction. These measures include, but are not limited to, access controls, anti-virus protection systems, and regular backup policies.",
          fr: "Uncommon12 S\u00e0RL met en \u0153uvre toutes les mesures techniques et organisationnelles appropri\u00e9es pour prot\u00e9ger les donn\u00e9es personnelles des membres contre tout acc\u00e8s non autoris\u00e9, perte, alt\u00e9ration ou destruction. Ces mesures incluent, mais ne sont pas limit\u00e9es \u00e0, des contr\u00f4les d'acc\u00e8s, des syst\u00e8mes de protection antivirus, et des politiques de sauvegarde r\u00e9guli\u00e8re.",
        },
      },
      {
        title: {
          en: "Deletion of data on termination",
          fr: "Suppression des donn\u00e9es en cas de r\u00e9siliation",
        },
        content: {
          en: "In the event of termination or deletion of membership, personal data will be deleted or anonymized within 90 days unless the law requires prolonged retention. Members may also request immediate deletion of their data by contacting Uncommon 12 by email at <a href=\"mailto:contact@uncommon12.ch\">contact@uncommon12.ch</a> or by post at Uncommon 12 S\u00e0rl, Chemin des Cr\u00eats de Pregny 13, 1218 Grand-Saconnex.",
          fr: "En cas de r\u00e9siliation ou de suppression du membership, les donn\u00e9es personnelles seront supprim\u00e9es ou anonymis\u00e9es dans un d\u00e9lai de 90 jours sauf si la loi impose une conservation prolong\u00e9e. Les membres peuvent \u00e9galement demander la suppression imm\u00e9diate de leurs donn\u00e9es en contactant Uncommon 12 par courriel \u00e0 <a href=\"mailto:contact@uncommon12.ch\">contact@uncommon12.ch</a> ou par courrier \u00e0 l'adresse suivante : Uncommon 12 S\u00e0rl, Chemin des Cr\u00eats de Pregny 13, 1218 Grand-Saconnex.",
        },
      },
      {
        title: {
          en: "Policy changes",
          fr: "Modifications de la politique",
        },
        content: {
          en: "Uncommon 12 S\u00e0rl reserves the right to modify this personal data collection and processing policy at any time. Changes will be communicated to members via the email address provided during registration.",
          fr: "Uncommon 12 S\u00e0rl se r\u00e9serve le droit de modifier cette politique de collecte et de traitement des donn\u00e9es personnelles \u00e0 tout moment. Les modifications seront communiqu\u00e9es aux membres via l'adresse email fournie lors de l'inscription.",
        },
      },
      {
        title: {
          en: "Contact",
          fr: "Contact",
        },
        content: {
          en: "For any questions or requests regarding this personal data protection policy, please contact Uncommon12 S\u00e0RL at the following address: Uncommon 12 S\u00e0rl, 13 Chemin des Cr\u00eats de Pregny, 1218 Grand-Saconnex.",
          fr: "Pour toute question ou demande concernant cette politique de protection des donn\u00e9es personnelles, veuillez contacter Uncommon12 S\u00e0RL \u00e0 l'adresse suivante : Uncommon 12 S\u00e0rl, 13 Chemin des Cr\u00eats de Pregny, 1218 Grand-Saconnex.",
        },
      },
    ],
  },
};

export const LEGAL_SLUGS = Object.keys(LEGAL_DOCUMENTS) as LegalSlug[];

export const resolveCanonicalLegalSlug = (slug: string): LegalSlug | undefined => {
  const normalized = slug.toLowerCase();

  // legacy aliases (old indexed URLs)
  if (LEGACY_LEGAL_ALIASES[normalized]) {
    return LEGACY_LEGAL_ALIASES[normalized];
  }

  // direct match
  if (LEGAL_SLUGS.includes(normalized as LegalSlug)) return normalized as LegalSlug;

  // localized match
  return LEGAL_SLUGS.find((key) => {
    const localized = LOCALIZED_LEGAL_SLUGS[key];
    return localized?.en === normalized || localized?.fr === normalized;
  });
};

export const getLegalDocument = (slug: string): LegalDocument | undefined => {
  const canonical = resolveCanonicalLegalSlug(slug);
  if (!canonical) return undefined;
  return LEGAL_DOCUMENTS[canonical];
};

export const getLegalNav = (locale: LocaleCode) =>
  LEGAL_SLUGS.map((slug) => ({
    slug,
    localizedSlug: getLocalizedLegalSlug(slug, locale),
    label: LEGAL_DOCUMENTS[slug].navLabel[locale] ?? LEGAL_DOCUMENTS[slug].navLabel.en ?? slug,
  }));

export const getLocalizedLegalSlug = (slug: LegalSlug, locale: LocaleCode): string =>
  LOCALIZED_LEGAL_SLUGS[slug][locale] ?? slug;

export const getLegalBasePath = (locale: LocaleCode) => {
  const base = locale === "fr" ? "/mentions-legales" : "/legals";
  return `/${locale}${base}`;
};

export const getLegalHref = (locale: LocaleCode, slug: LegalSlug) =>
  `${getLegalBasePath(locale)}/${getLocalizedLegalSlug(slug, locale)}`;

export const getLegalIndexHref = (locale: LocaleCode) => getLegalBasePath(locale);
